import intl from 'components/i18n/ReactIntlWrapper';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { Box, Button, Grid, SelectProps, FormControlLabel, Switch } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { Client, CorporateEntity, CorporateEntitySearchResponseResponse, Employee, ListOptionDTO, ListOptionDTOSearchResponseResponse, Provider, UserDetail } from 'axiosApi/models';
import TypeAhead, { AutocompleteCustomProps } from 'components/common/TypeAhead';
import Api from 'axiosApi/api';
import { useApi } from 'api/ApiProvider';
import { ENTITY_TYPE } from 'common/constants';
import AutocompleteStored, { AutocompleteStoredProps } from 'components/common/AutocompleteStored';
import { useDialog } from 'common/DialogProvider';
import { AxiosResponse } from 'axios';

type UserEntity = UserDetail;

type UserEntitySelectorProps = {
    userEntity: UserDetail;
    setUserEntity: (userEntity: UserDetail) => void;
    entityTypeAheadProps?: Partial<AutocompleteCustomProps<UserEntity, false, false, false>>
    roleAutocompleteProps?: Partial<AutocompleteStoredProps<true, false, false>>;
    onDelete?: (userEntity: UserEntity) => void;
    disabledDelete?: boolean;
    onDefaultChange?: (userEntity: UserDetail, isDefault: boolean) => void;
}

const UserEntitySelector = ({ userEntity, setUserEntity, entityTypeAheadProps, onDelete, disabledDelete, roleAutocompleteProps, onDefaultChange }: UserEntitySelectorProps) => {
    const toast = useToastMessageQueue();

    const { clientsApi, employeeApi, invoiceApi } : Api = useApi();

    const corporateEntityToListOptionDTOResponse = (response: any): any => {
      const queryResult = response?.data?.data?.queryResult || [];
      
      const transformedEntities = queryResult.map(
        (entity: CorporateEntity): ListOptionDTO => ({
          id: entity.id,
          label: entity.name || '',
        })
      );
      
      // Return in the format expected by TypeAhead: response.data.data should be an array
      // TypeAhead extracts const { data } = response?.data || {}; and expects data to be an array
      return {
        data: {
          data: transformedEntities  // data should be nested: response.data.data = array
        }
      };
    };

    const providerToListOptionDTOResponse = (response: any): any => {
      // Provider API returns ListOptionDTOSearchResponseResponse with nested structure
      // Try multiple paths to find the queryResult
      const queryResult = response?.data?.data?.data?.queryResult || 
                         response?.data?.data?.queryResult || 
                         response?.data?.queryResult || 
                         [];
      
      // Return in the format expected by TypeAhead: response.data.data should be an array
      // TypeAhead extracts const { data } = response?.data || {}; and expects data to be an array
      return {
        data: {
          data: queryResult  // queryResult already contains ListOptionDTO objects with id and label
        }
      };
    };

    const clientToListOptionDTOResponse = (response: any): any => {
      // Client API returns ClientListResponse: { data: Client[] }
      const clientData = response?.data?.data || [];
      
      const transformedClients = clientData.map(
        (client: Client): ListOptionDTO => ({
          id: client.id,
          label: client.name ? `${client.name} (${client.tradeName || ''})` : '',
        })
      );
      
      // Return in the format expected by TypeAhead: response.data.data should be an array
      return {
        data: {
          data: transformedClients
        }
      };
    };

    const employeeToListOptionDTOResponse = (response: any): any => {
      // Employee API returns EmployeeListResponse: { data: Employee[] }
      const employeeData = response?.data?.data || [];
      
      const transformedEmployees = employeeData.map(
        (employee: Employee): ListOptionDTO => ({
          id: employee.id,
          label: employee.name ? `${employee.name} ${employee.lastName || ''}` : '',
        })
      );
      
      // Return in the format expected by TypeAhead: response.data.data should be an array
      return {
        data: {
          data: transformedEmployees
        }
      };
    };    const PROMISES: Partial<Record<ENTITY_TYPE, (searchTerm: string) => Promise<any>>> = {
        [ENTITY_TYPE.CLIENT]: (searchTerm: string) => 
            clientsApi?.apiVversionClientTypeaheadTextGet(searchTerm, "1")
                .then(clientToListOptionDTOResponse)
                .catch((error) => {
                    console.error('Error fetching clients:', error);
                    return {
                        data: {
                            data: []
                        }
                    };
                }),
        [ENTITY_TYPE.EMPLOYEE]: (searchTerm: string) => 
            employeeApi?.apiVversionEmployeeTypeaheadTextGet(searchTerm, "1")
                .then(employeeToListOptionDTOResponse)
                .catch((error) => {
                    console.error('Error fetching employees:', error);
                    return {
                        data: {
                            data: []
                        }
                    };
                }),
        [ENTITY_TYPE.PROVIDER]: (searchTerm: string) => 
            employeeApi?.apiVversionListProvidersTypeAheadGet("1", searchTerm)
                .then(providerToListOptionDTOResponse)
                .catch((error) => {
                    console.error('Error fetching providers:', error);
                    // Return empty result structure to prevent filter errors
                    return {
                        data: {
                            data: []
                        }
                    };
                }),
        [ENTITY_TYPE.CORPORATE_ENTITY]: (searchTerm: string) => 
            invoiceApi?.apiVversionCorporateEntitySearchGet("1", searchTerm)
                .then(corporateEntityToListOptionDTOResponse)
                .catch((error) => {
                    console.error('Error fetching corporate entities:', error);
                    // Return empty result structure to prevent filter errors
                    return {
                        data: {
                            data: []
                        }
                    };
                })
    };

    const TypeAheadLabel: Partial<Record<ENTITY_TYPE, string>> = {
        [ENTITY_TYPE.CLIENT]: intl.get("reportsFilterComponent.clients"),
        [ENTITY_TYPE.EMPLOYEE]: intl.get('employeeProjectForm.placeholder.employee'),
        [ENTITY_TYPE.PROVIDER]: intl.get('providerSelect.placeholder.provider'),
        [ENTITY_TYPE.CORPORATE_ENTITY]: intl.get('corporateEntitySelect.placeholder.corporateEntity')
    };

    const { openDialog } = useDialog();

    const handleDelete = (userEntity: UserEntity) => {
        if (onDelete) {
            openDialog({
                title: intl.get('delete.modal.title'),
                message: intl.get('delete.modal.userAsEmployee.message'),
                type: 'confirm',
                confirmButton: {
                    confirmLabel: intl.get('accept.button'),
                    handleConfirm: (message) => {
                        message.handleClose(message);
                        onDelete(userEntity);
                    }
                },
                cancelButton: {
                    cancelLabel: intl.get('cancel.button'),
                    handleCancel: (message) => {
                        message.handleClose(message);
                    }
                }
            });
            return;
        }
    };

    const onChangeRole = (newRoles: number[]) => {
        setUserEntity({
            ...userEntity,
            securityRoles: newRoles
        });
    }

    const handleDefaultToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isDefault = event.target.checked;
        
        // Notify parent component about the default change
        // The parent will handle updating the state to ensure only one default
        if (onDefaultChange) {
            onDefaultChange(userEntity, isDefault);
        }
    };

    const OptionLabelByEntityType : Partial<Record<ENTITY_TYPE, (option: any) => string>> = {
        [ENTITY_TYPE.CLIENT]: (option: any) => option?.label || option?.entityName || (option?.name && `${(option as Client)?.name} (${(option as Client)?.tradeName})`) || (option?.entityId ? `No client` : ``),
        [ENTITY_TYPE.EMPLOYEE]: (option: any) => option?.label || option?.entityName || (option?.name && `${(option as Employee)?.name} ${(option as Employee)?.lastName}`) || (option?.entityId ? `No employee` : ``),
        [ENTITY_TYPE.PROVIDER]: (option: any) => option?.label || option?.entityName || (option?.name && `${(option as Provider)?.name}`) || (option?.entityId ? `No provider` : ``),
        [ENTITY_TYPE.CORPORATE_ENTITY]: (option: any) => option?.label || option?.entityName || (option?.name && `${(option as CorporateEntity)?.name}`) || (option?.entityId ? `No corporate entity` : ``)
    };
    
    return (userEntity &&
    <Box display={'flex'}>
        <Grid container spacing={2} alignItems='center' width='100%'>
            <Grid size={4}>
                <TypeAhead
                    apiPromise={PROMISES[userEntity.entityType]}
                    getOptionLabel={OptionLabelByEntityType[userEntity.entityType]}
                    label={TypeAheadLabel[userEntity.entityType]}
                    value={userEntity}
                    {...entityTypeAheadProps}
                    onChange={(event, newValue) => {
                        if (newValue) {
                            console.log('newValue', newValue);
                            setUserEntity({
                                id: userEntity.id, // Preserve the original userEntity.id (association record ID)
                                idUser: userEntity.idUser,
                                entityType: userEntity.entityType,
                                entityId: newValue.id, // Corporate Entity ID goes here
                                entityName: (newValue as any).label || newValue.entityName || '', // Cast to any to access label property
                                securityRoles: userEntity.securityRoles || [],
                                isDefault: userEntity.isDefault || false,
                            });
                        }
                    }}
                />
            </Grid>
            <Grid size={4}>
                <AutocompleteStored
                    multiple
                    label={intl.get('userSelectRole.label')}
                    queryType='roles'
                    getOptionKey={(option => option?.id)}
                    getOptionLabel={(option) => option?.name || ''}
                    value={userEntity.securityRoles || []}
                    disableClearable
                    onChange={(event, newValue) => {
                        onChangeRole(newValue.map(role => role.id));
                    }}
                    {...roleAutocompleteProps}
                />
            </Grid>
            <Grid size={4} display="flex" justifyContent="center">
                <FormControlLabel
                    control={
                        <Switch
                            checked={userEntity.isDefault || false}
                            onChange={handleDefaultToggle}
                            size="small"
                            disabled={userEntity.entityId === 0 || !userEntity.entityId}
                        />
                    }
                    label={intl.get('default')}
                    labelPlacement="top"
                />
            </Grid>
        </Grid>
        <Box display={'flex'} alignItems='center' ml={1}>
            <Button
                variant='text'
                color='inherit'
                title={intl.get('employeeRow.deleteButton.title')}
                onClick={() => handleDelete(userEntity)}
                disabled={onDelete ? disabledDelete : true}
            >
                <Delete/>
            </Button>
        </Box>
    </Box>)
}

export default UserEntitySelector;
